import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShoppingCart, Store, Search, Plus, Minus, Trash2, Save, RefreshCw, Info,
  ShieldAlert, UploadCloud, X, Bike, Utensils, CheckCircle2, ChevronRight, History,
} from "lucide-react";
import { Card, Button, Badge, Spinner } from "../lib/ui";
import { useToast } from "../lib/toast";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const rp = (n) => "Rp " + Math.round(Number(n) || 0).toLocaleString("id-ID");

const PreviewRow = ({ label, value, muted }) => (
  <div className={`flex items-center justify-between ${muted ? "text-ink-mute" : "text-ink-soft"}`}>
    <span>{label}</span><span className="font-medium">{value}</span>
  </div>
);
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "-");
const CH_ICON = { offline: Store, grab: Bike, gofood: Utensils };
const CH_COLOR = { offline: "text-rose-600", grab: "text-emerald-600", gofood: "text-red-500", shopeefood: "text-orange-500" };
const PAYMENT_OPTS = [
  { key: "cash", label: "Tunai" },
  { key: "transfer", label: "Transfer" },
  { key: "qris", label: "QRIS" },
  { key: "debit", label: "Debit" },
  { key: "credit", label: "Kredit" },
];

export default function SalesInput() {
  const toast = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [channel, setChannel] = useState("offline");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [menus, setMenus] = useState([]);
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);            // [{menu_item_id,name,category,station,qty}]
  const [totalSales, setTotalSales] = useState("");
  const [note, setNote] = useState("");
  const [receipt, setReceipt] = useState(null);    // base64 data URL
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taxCfg, setTaxCfg] = useState(null);   // { pb1_pct, service_pct, marketplace }

  const today = new Date();
  const dateStr = today.toISOString().slice(0, 10);

  const loadDynamic = useCallback(async () => {
    const [sm, inc] = await Promise.all([
      api.get("/sales/pos-entry/summary", { params: { date: dateStr } }),
      api.get("/incidents", { params: { type_key: "CASHIER_INCIDENT" } }),
    ]);
    setSummary(sm.data);
    setIncidents(inc.data.slice(0, 4));
  }, [dateStr]);

  useEffect(() => {
    (async () => {
      try {
        const [ch, mn] = await Promise.all([
          api.get("/sales/channels"),
          api.get("/menu-items?active_only=true"),
        ]);
        const active = ch.data.filter((c) => c.enabled);
        setChannels(active);
        if (active.length) setChannel(active[0].key);
        setMenus(mn.data);
        try { const tc = await api.get("/sales/tax-config"); setTaxCfg(tc.data); } catch { /* preview optional */ }
        await loadDynamic();
      } catch { toast.error("Gagal memuat data"); } finally { setLoading(false); }
    })();
  }, [loadDynamic, toast]);

  const results = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    const inCart = new Set(cart.map((c) => c.menu_item_id));
    return menus.filter((m) => m.name.toLowerCase().includes(q) && !inCart.has(m.id)).slice(0, 8);
  }, [search, menus, cart]);

  const preview = useMemo(() => {
    const bill = Number(totalSales) || 0;
    if (!taxCfg || bill <= 0) return null;
    if (channel === "offline") {
      const service = bill * (taxCfg.service_pct || 0) / 100;
      const subtotal = bill + service;
      const pb1 = subtotal * (taxCfg.pb1_pct || 0) / 100;
      return { online: false, bill, service, service_pct: taxCfg.service_pct || 0, subtotal, pb1, pb1_pct: taxCfg.pb1_pct || 0, customer: subtotal + pb1 };
    }
    const comm = (taxCfg.marketplace || {})[channel] || 0;
    const fee = bill * comm / 100;
    return { online: true, bill, comm, fee, net: bill - fee, customer: bill };
  }, [totalSales, channel, taxCfg]);

  const addMenu = (m) => {
    setCart((prev) => prev.some((c) => c.menu_item_id === m.id)
      ? prev.map((c) => c.menu_item_id === m.id ? { ...c, qty: c.qty + 1 } : c)
      : [...prev, { menu_item_id: m.id, name: m.name, category: m.category, station: m.station, qty: 1 }]);
    setSearch("");
  };
  const setQty = (id, delta) => setCart((prev) => prev
    .map((c) => c.menu_item_id === id ? { ...c, qty: Math.max(1, c.qty + delta) } : c));
  const removeItem = (id) => setCart((prev) => prev.filter((c) => c.menu_item_id !== id));
  const totalQty = cart.reduce((s, c) => s + c.qty, 0);

  const onReceipt = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Ukuran maksimal 5MB"); return; }
    const r = new FileReader();
    r.onload = () => setReceipt(r.result);
    r.readAsDataURL(f);
  };

  const save = async () => {
    if (!cart.length) { toast.error("Tambahkan minimal satu menu"); return; }
    if (totalSales === "" || Number(totalSales) < 0) { toast.error("Isi Total Bill"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/sales/pos-entry", {
        date: dateStr, channel,
        payment_method: channel === "offline" ? paymentMethod : null,
        items: cart.map((c) => ({ menu_item_id: c.menu_item_id, quantity: c.qty })),
        total_sales: Number(totalSales), note: note || null, receipt_photo: receipt || null,
      });
      const missing = data?.consumption?.missing_recipe_menus || [];
      toast.success(`Penjualan tersimpan · ${data.consumption.products_consumed} bahan terpotong stok`);
      if (missing.length) toast.error(`Resep belum diset: ${missing.join(", ")} (stok tidak terpotong)`);
      setCart([]); setTotalSales(""); setNote(""); setReceipt(null);
      loadDynamic();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); } finally { setSaving(false); }
  };

  const commissionChannels = channels.filter((c) => (c.commission_pct || 0) > 0);
  const stMap = { PENDING: "Open", UNDER_REVIEW: "Open" };
  const isOpen = (s) => s === "PENDING" || s === "UNDER_REVIEW";

  if (loading) return <div className="flex h-64 items-center justify-center"><Spinner className="h-8 w-8" /></div>;

  return (
    <div data-testid="sales-input-page">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-ink">Sales Input</h1>
          <p className="text-sm text-ink-soft">Input penjualan setelah transaksi di POS Popcorn selesai.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate("/sales-history")} data-testid="goto-history">
            <History size={14} /> Sales History
          </Button>
          <div className="flex items-center gap-2 rounded-xl border border-line bg-surface px-3 py-1.5 text-sm">
            <Store size={15} className="text-primary" />
            <span className="font-semibold text-ink">{user?.branch_name || (typeof user?.branch === "string" ? user.branch : user?.branch?.name) || "Cabang"}</span>
            <span className="text-ink-mute">· {dateStr}</span>
          </div>
        </div>
      </div>

      {(summary?.missing_recipe_menus || []).length > 0 && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="missing-recipe-banner">
          <ShieldAlert size={16} className="mt-0.5 shrink-0" />
          <span><b>Resep belum diset ({summary.missing_recipe_menus.length}):</b> {summary.missing_recipe_menus.join(", ")}. Menu ini terjual hari ini tetapi stok tidak terpotong (BOM belum ada). Segera lengkapi resepnya.</span>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* LEFT — Input Penjualan */}
        <Card data-testid="sales-input-form">
          <div className="mb-3 flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-ink text-xs font-bold text-white">1</span>
            <div>
              <h2 className="font-heading font-bold text-ink">Input Penjualan</h2>
              <p className="text-xs text-ink-soft">Pilih channel penjualan dan masukkan menu yang terjual.</p>
            </div>
          </div>

          <p className="mb-1.5 text-xs font-semibold text-ink-soft">Pilih Channel</p>
          <div className="mb-4 grid grid-cols-3 gap-2">
            {channels.map((c) => {
              const Icon = CH_ICON[c.key] || Store;
              const on = channel === c.key;
              return (
                <button key={c.key} onClick={() => setChannel(c.key)} data-testid={`channel-btn-${c.key}`}
                  className={`flex items-center justify-center gap-2 rounded-xl border-2 px-3 py-3 text-sm font-semibold transition ${on ? "border-primary bg-primary/5 text-primary" : "border-line text-ink-soft hover:border-ink-mute"}`}>
                  <Icon size={16} className={on ? "text-primary" : CH_COLOR[c.key]} /> {c.label}
                </button>
              );
            })}
          </div>

          {channel === "offline" && (
            <div className="mb-4" data-testid="payment-method-block">
              <p className="mb-1.5 text-xs font-semibold text-ink-soft">Metode Pembayaran</p>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                {PAYMENT_OPTS.map((p) => {
                  const on = paymentMethod === p.key;
                  return (
                    <button key={p.key} onClick={() => setPaymentMethod(p.key)} data-testid={`payment-btn-${p.key}`}
                      className={`rounded-xl border-2 px-2 py-2 text-xs font-semibold transition ${on ? "border-primary bg-primary/5 text-primary" : "border-line text-ink-soft hover:border-ink-mute"}`}>
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <p className="mb-1.5 text-xs font-semibold text-ink-soft">Tambah Menu</p>
          <div className="relative mb-2">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-mute" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari menu..."
              data-testid="menu-search"
              className="w-full rounded-xl border border-line bg-app py-2.5 pl-9 pr-3 text-sm outline-none focus:border-primary" />
            {results.length > 0 && (
              <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-xl border border-line bg-surface shadow-lg" data-testid="menu-search-results">
                {results.map((m) => (
                  <button key={m.id} onClick={() => addMenu(m)} data-testid={`menu-add-${m.id}`}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-app">
                    <span className="font-medium text-ink">{m.name}</span>
                    <span className="flex items-center gap-1 text-xs text-ink-mute">{cap(m.station)} <Plus size={13} className="text-primary" /></span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="overflow-hidden rounded-xl border border-line">
            <table className="w-full text-sm">
              <thead className="bg-app text-left text-xs uppercase text-ink-mute">
                <tr><th className="px-3 py-2">Menu</th><th className="px-3 py-2">Kategori</th><th className="px-3 py-2 text-center">Qty</th><th className="px-3 py-2 text-center">Aksi</th></tr>
              </thead>
              <tbody className="divide-y divide-line">
                {cart.length === 0 && <tr><td colSpan={4} className="px-3 py-8 text-center text-sm text-ink-mute">Belum ada menu. Cari & tambahkan menu terjual.</td></tr>}
                {cart.map((c) => (
                  <tr key={c.menu_item_id} data-testid={`cart-row-${c.menu_item_id}`}>
                    <td className="px-3 py-2.5 font-medium text-ink">{c.name}</td>
                    <td className="px-3 py-2.5 text-ink-soft">{cap(c.station)}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-center gap-1.5">
                        <button onClick={() => setQty(c.menu_item_id, -1)} data-testid={`cart-dec-${c.menu_item_id}`} className="rounded-lg border border-line p-1 text-ink-soft hover:bg-app"><Minus size={13} /></button>
                        <span className="w-7 text-center font-semibold tabular-nums text-ink" data-testid={`cart-qty-${c.menu_item_id}`}>{c.qty}</span>
                        <button onClick={() => setQty(c.menu_item_id, 1)} data-testid={`cart-inc-${c.menu_item_id}`} className="rounded-lg border border-line p-1 text-ink-soft hover:bg-app"><Plus size={13} /></button>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <button onClick={() => removeItem(c.menu_item_id)} data-testid={`cart-remove-${c.menu_item_id}`} className="rounded-lg p-1.5 text-danger-text hover:bg-red-50"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="flex items-center justify-between rounded-xl bg-app px-3 py-2.5">
              <span className="text-xs font-medium text-ink-soft">Total Item (Qty)</span>
              <span className="font-heading text-lg font-bold text-ink" data-testid="total-item-qty">{totalQty}</span>
            </div>
            <div>
              <label className="text-xs font-semibold text-ink-soft">Total Bill (Rp) <span className="text-primary">*</span></label>
              <input type="number" min="0" value={totalSales} onChange={(e) => setTotalSales(e.target.value)}
                data-testid="total-sales-input" placeholder="0"
                className="mt-0.5 w-full rounded-xl border border-line bg-surface px-3 py-2 text-right font-semibold text-ink outline-none focus:border-primary" />
              <p className="mt-0.5 text-[11px] text-ink-mute">Sebelum PB1 &amp; Service Charge</p>
            </div>
          </div>
          <p className="mt-1 text-[11px] text-ink-mute">* Masukkan <b>Total Bill</b> (sebelum Service Charge &amp; PB1) sesuai laporan POS Popcorn. Service Charge &amp; PB1 dihitung otomatis oleh sistem.</p>

          {preview && (
            <div className="mt-3 rounded-xl border border-primary/20 bg-primary/5 p-3" data-testid="pos-preview">
              <p className="mb-1.5 text-xs font-bold text-primary">Rincian Otomatis (read-only)</p>
              {!preview.online ? (
                <div className="space-y-0.5 text-sm">
                  <PreviewRow label="Bill" value={rp(preview.bill)} />
                  <PreviewRow label={`Service Charge (${preview.service_pct}%)`} value={rp(preview.service)} />
                  <PreviewRow label="Subtotal" value={rp(preview.subtotal)} muted />
                  <PreviewRow label={`PB1 (${preview.pb1_pct}%)`} value={rp(preview.pb1)} />
                  <div className="mt-1 flex items-center justify-between border-t border-primary/20 pt-1 font-bold text-ink" data-testid="pos-preview-customer">
                    <span>Customer Payment</span><span>{rp(preview.customer)}</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-0.5 text-sm">
                  <PreviewRow label="Gross Sales" value={rp(preview.bill)} />
                  <PreviewRow label={`Marketplace Fee (${preview.comm}%)`} value={`- ${rp(preview.fee)}`} />
                  <div className="mt-1 flex items-center justify-between border-t border-primary/20 pt-1 font-bold text-ink" data-testid="pos-preview-net">
                    <span>Net Settlement</span><span>{rp(preview.net)}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div>
              <p className="mb-1.5 text-xs font-semibold text-ink-soft">Upload Foto Struk (Opsional)</p>
              {receipt ? (
                <div className="relative h-28 overflow-hidden rounded-xl border border-line">
                  <img src={receipt} alt="struk" className="h-full w-full object-cover" />
                  <button onClick={() => setReceipt(null)} data-testid="receipt-remove" className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white"><X size={12} /></button>
                </div>
              ) : (
                <label data-testid="receipt-photo" className="flex h-28 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-line text-ink-mute hover:border-primary hover:text-primary">
                  <UploadCloud size={20} />
                  <span className="text-[11px]">Klik untuk upload foto struk</span>
                  <span className="text-[10px]">PNG, JPG maks 5MB</span>
                  <input type="file" accept="image/*" className="hidden" onChange={onReceipt} />
                </label>
              )}
            </div>
            <div>
              <p className="mb-1.5 text-xs font-semibold text-ink-soft">Catatan (Opsional)</p>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} placeholder="Tulis catatan jika ada..."
                data-testid="sales-note" className="w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-primary" />
            </div>
          </div>

          <Button onClick={save} loading={saving} className="mt-3 w-full" data-testid="save-sale-btn">
            <Save size={16} /> Simpan Penjualan
          </Button>
        </Card>

        {/* RIGHT — summary + commission + incident */}
        <div className="space-y-4">
          <Card data-testid="sales-summary">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-ink text-xs font-bold text-white">2</span>
                <div>
                  <h2 className="font-heading font-bold text-ink">Ringkasan Penjualan Hari Ini</h2>
                  <p className="text-xs text-ink-soft">Ringkasan otomatis dari semua input penjualan hari ini.</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={loadDynamic} data-testid="summary-refresh"><RefreshCw size={14} /> Refresh</Button>
            </div>
            <table className="w-full text-sm">
              <thead className="border-b border-line text-left text-xs uppercase text-ink-mute">
                <tr><th className="py-2">Channel</th><th className="py-2 text-center">Total Qty</th><th className="py-2 text-right">Total Penjualan (Rp)</th></tr>
              </thead>
              <tbody className="divide-y divide-line">
                {(summary?.channels || []).map((c) => (
                  <tr key={c.key} data-testid={`summary-row-${c.key}`}>
                    <td className="py-2.5 font-medium text-ink">{c.label}</td>
                    <td className="py-2.5 text-center tabular-nums text-ink-soft">{c.qty || 0}</td>
                    <td className="py-2.5 text-right tabular-nums font-semibold text-ink">{rp(c.total_sales)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-primary/20 bg-primary/5" data-testid="summary-total">
                  <td className="py-2.5 font-heading font-bold text-primary">TOTAL HARI INI</td>
                  <td className="py-2.5 text-center font-bold tabular-nums text-primary">{summary?.total_qty || 0}</td>
                  <td className="py-2.5 text-right font-heading font-bold text-primary">{rp(summary?.total_sales)}</td>
                </tr>
              </tfoot>
            </table>
          </Card>

          {commissionChannels.length > 0 && (
            <Card data-testid="commission-info">
              <h3 className="mb-2 text-sm font-bold text-ink">Informasi Komisi Marketplace <span className="font-normal text-ink-mute">(Informasi Saja)</span></h3>
              <div className="grid grid-cols-2 gap-2">
                {commissionChannels.map((c) => (
                  <div key={c.key} className="flex items-center justify-between rounded-xl border border-line px-3 py-2.5">
                    <span className="text-sm font-semibold text-ink">{c.label}</span>
                    <span className="text-right"><span className="block text-[10px] text-ink-mute">Komisi</span><span className="font-heading text-lg font-bold text-ink">{c.commission_pct}%</span></span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card data-testid="incident-quicklist">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-ink text-xs font-bold text-white">3</span>
                <h2 className="font-heading font-bold text-ink">Incident</h2>
              </div>
              <Button variant="secondary" size="sm" onClick={() => navigate("/sales-incident")} data-testid="goto-incident">
                <ShieldAlert size={14} /> Buat / Lihat Semua <ChevronRight size={14} />
              </Button>
            </div>
            {incidents.length === 0 ? (
              <p className="py-3 text-center text-sm text-ink-mute">Belum ada incident hari ini.</p>
            ) : (
              <div className="divide-y divide-line">
                {incidents.map((i) => (
                  <div key={i.id} className="flex items-center justify-between py-2 text-sm" data-testid={`incident-quick-${i.id}`}>
                    <span className="text-ink">{i.category || i.type_name}</span>
                    <Badge tone={isOpen(i.status) ? "warn" : "success"}>{isOpen(i.status) ? "Open" : "Closed"}</Badge>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl bg-blue-50 px-4 py-3 text-[11px] text-blue-800" data-testid="sales-input-notes">
        <span className="flex items-center gap-1 font-semibold"><Info size={14} /> Catatan Penting:</span>
        <span className="flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-600" /> Setiap penjualan otomatis memotong stok via Recipe/BOM.</span>
        <span className="flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-600" /> Stok berkurang real-time.</span>
        <span className="flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-600" /> Terhubung dengan Inventory, Dashboard, Forecasting & Finance.</span>
        <span className="flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-600" /> Semua modul memakai satu Master Menu yang sama.</span>
      </div>
    </div>
  );
}
